import {
  BoxGeometry,
  CapsuleGeometry,
  ConeGeometry,
  CylinderGeometry,
  DoubleSide,
  Group,
  Mesh,
  MeshStandardMaterial,
  SphereGeometry,
  TorusGeometry,
  type BufferGeometry,
} from "three";

import type { ResidentPresentation } from "../../sim/neighborhood/index.ts";
import {
  sameCharacterAppearance,
  type CharacterAppearance,
} from "../../sim/appearance.ts";
import { NEIGHBORHOOD_CONFIG } from "../../config/neighborhood.ts";
import { CHARACTER_PRESENTATION_CONFIG } from "../../config/character.ts";
import { APPEARANCE_PRESENTATION_CONFIG } from "../../config/appearance.ts";
import {
  applyGarmentLayerSet,
  createGarmentGeometrySet,
  createGarmentLayerSet,
  disposeGarmentGeometrySet,
  type GarmentLayerSet,
} from "../characterView/garments.ts";
import {
  createCharacterFeatureGeometrySet,
  disposeCharacterFeatureGeometrySet,
} from "../characterView/features.ts";

interface ActorMaterials {
  readonly outerwear: MeshStandardMaterial;
  readonly lower: MeshStandardMaterial;
  readonly accent: MeshStandardMaterial;
  readonly skin: MeshStandardMaterial;
  readonly hair: MeshStandardMaterial;
  readonly eyes: MeshStandardMaterial;
  readonly accessory: MeshStandardMaterial;
  readonly boot: MeshStandardMaterial;
  readonly trim: MeshStandardMaterial;
}

interface ActorParts {
  readonly presentationRoot: Group;
  readonly upperBody: Group;
  readonly hips: Mesh;
  readonly torso: Mesh;
  readonly garments: GarmentLayerSet;
  readonly belly: Mesh;
  readonly leftArm: Group;
  readonly rightArm: Group;
  readonly leftSleeve: Mesh;
  readonly rightSleeve: Mesh;
  readonly leftHand: Mesh;
  readonly rightHand: Mesh;
  readonly leftLeg: Group;
  readonly rightLeg: Group;
  readonly skirt: Mesh;
  readonly head: Mesh;
  readonly nose: Mesh;
  readonly mouth: Mesh;
  readonly leftBrow: Mesh;
  readonly rightBrow: Mesh;
  readonly leftEye: Mesh;
  readonly rightEye: Mesh;
  readonly hair: Mesh;
  readonly leftLens: Mesh;
  readonly rightLens: Mesh;
  readonly headwear: Mesh;
  readonly neckwear: Mesh;
  readonly carried: Mesh;
  readonly carriedHandle: Mesh;
  readonly carriedFlap: Mesh;
}

interface Actor {
  readonly object: Group;
  readonly materials: ActorMaterials;
  readonly parts: ActorParts;
  readonly slotIndex: number;
  residentId: string | null;
}

interface SharedGeometry {
  readonly hips: BufferGeometry;
  readonly head: BufferGeometry;
  readonly torso: BufferGeometry;
  readonly belly: BufferGeometry;
  readonly leg: BufferGeometry;
  readonly boot: BufferGeometry;
  readonly hand: BufferGeometry;
  readonly brow: BufferGeometry;
  readonly mouth: BufferGeometry;
  readonly skirt: BufferGeometry;
  readonly nose: BufferGeometry;
  readonly eye: BufferGeometry;
  readonly roundLens: BufferGeometry;
  readonly squareLens: BufferGeometry;
  readonly neckwear: BufferGeometry;
  readonly carried: BufferGeometry;
  readonly carriedHandle: BufferGeometry;
  readonly carriedFlap: BufferGeometry;
}

function createMaterial(
  name: string,
  colour: number,
  roughness = 0.94,
): MeshStandardMaterial {
  return new MeshStandardMaterial({
    name,
    color: colour,
    roughness,
    metalness: 0,
  });
}

function setShadow(mesh: Mesh): Mesh {
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

function hangingCylinderGeometry(
  lengthMeters: number,
  topRadiusMeters: number,
  bottomRadiusMeters: number,
): BufferGeometry {
  const geometry = new CylinderGeometry(
    topRadiusMeters,
    bottomRadiusMeters,
    lengthMeters,
    6,
  );
  geometry.translate(0, -lengthMeters * 0.5, 0);
  return geometry;
}

const NPC_HEAD_RADIUS_M =
  0.245 * CHARACTER_PRESENTATION_CONFIG.proportions.headScale;
const NPC_LEG_PIVOT_HEIGHT_M = 0.72;
const NPC_BOOT_CENTER_Y_M = -0.65;
const NPC_BOOT_CENTER_Z_M = 0.06;
const NPC_BOOT_SCALE = CHARACTER_PRESENTATION_CONFIG.proportions.bootScale;
const NPC_BOOT_HEIGHT_M = 0.14 * NPC_BOOT_SCALE;
const NPC_BOOT_LENGTH_M = 0.29 * NPC_BOOT_SCALE;

function capsuleBootGeometry(): BufferGeometry {
  const geometry = new CapsuleGeometry(
    0.07 * NPC_BOOT_SCALE,
    0.15 * NPC_BOOT_SCALE,
    2,
    7,
  );
  geometry.rotateX(Math.PI * 0.5);
  return geometry;
}

function browGeometry(): BufferGeometry {
  const geometry = new CapsuleGeometry(0.008, 0.062, 1, 4);
  geometry.rotateZ(Math.PI * 0.5);
  return geometry;
}

function npcBootBottomHeightM(legAngleRadians: number): number {
  const cosine = Math.cos(legAngleRadians);
  const sine = Math.sin(legAngleRadians);
  const centerY = NPC_LEG_PIVOT_HEIGHT_M +
    NPC_BOOT_CENTER_Y_M * cosine -
    NPC_BOOT_CENTER_Z_M * sine;
  const verticalExtent =
    NPC_BOOT_HEIGHT_M * 0.5 * Math.abs(cosine) +
    NPC_BOOT_LENGTH_M * 0.5 * Math.abs(sine);
  return centerY - verticalExtent;
}

function npcGroundOffsetM(leftLegX: number, rightLegX: number): number {
  const lowestBootM = Math.min(
    npcBootBottomHeightM(leftLegX),
    npcBootBottomHeightM(rightLegX),
  );
  return CHARACTER_PRESENTATION_CONFIG.soleClearanceMeters +
    Math.max(0, -lowestBootM);
}

function stableMotionPhase(residentId: string): number {
  let hash = 2_166_136_261;
  for (let index = 0; index < residentId.length; index += 1) {
    hash ^= residentId.charCodeAt(index);
    hash = Math.imul(hash, 16_777_619);
  }
  return ((hash >>> 0) / 0xffff_ffff) * Math.PI * 2;
}

function stableMotionVariation(residentId: string): number {
  return (stableMotionPhase(residentId + ":cadence") / (Math.PI * 2)) *
    0.18 - 0.09;
}

function setHeadVerticalOffset(parts: ActorParts, offsetMeters: number): void {
  parts.head.position.y = 1.72 + offsetMeters;
  parts.nose.position.y = 1.715 + offsetMeters;
  parts.mouth.position.y = 1.635 + offsetMeters;
  parts.leftEye.position.y = 1.765 + offsetMeters;
  parts.rightEye.position.y = 1.765 + offsetMeters;
  parts.leftBrow.position.y = 1.825 + offsetMeters;
  parts.rightBrow.position.y = 1.825 + offsetMeters;
  parts.hair.position.y = 1.72 + offsetMeters;
  parts.leftLens.position.y = 1.765 + offsetMeters;
  parts.rightLens.position.y = 1.765 + offsetMeters;
  parts.headwear.position.y = 1.72 + offsetMeters;
  parts.neckwear.position.y = 1.52 + offsetMeters;
}

export class NpcPopulationView {
  readonly object = new Group();
  readonly #actors: readonly Actor[];
  readonly #actorByResidentId = new Map<string, Actor>();
  readonly #garmentGeometries = createGarmentGeometrySet();
  readonly #featureGeometries = createCharacterFeatureGeometrySet(
    NPC_HEAD_RADIUS_M,
  );
  readonly #shared: SharedGeometry = {
    hips: new CylinderGeometry(0.19, 0.23, 0.22, 7),
    head: new SphereGeometry(NPC_HEAD_RADIUS_M, 12, 9),
    torso: new CylinderGeometry(0.235, 0.265, 0.56, 8),
    belly: new SphereGeometry(0.24, 8, 5),
    leg: hangingCylinderGeometry(
      0.7,
      0.105 * CHARACTER_PRESENTATION_CONFIG.proportions.legWidthScale,
      0.082 * CHARACTER_PRESENTATION_CONFIG.proportions.legWidthScale,
    ),
    boot: capsuleBootGeometry(),
    hand: new SphereGeometry(0.068, 7, 5),
    brow: browGeometry(),
    mouth: new TorusGeometry(0.05, 0.007, 4, 9, Math.PI),
    skirt: new ConeGeometry(0.34, 0.62, 7),
    nose: new ConeGeometry(0.042, 0.135, 6),
    eye: new SphereGeometry(0.033, 7, 5),
    roundLens: new TorusGeometry(0.07, 0.012, 4, 8),
    squareLens: new TorusGeometry(0.07, 0.012, 4, 8),
    neckwear: new TorusGeometry(0.205, 0.045, 4, 8),
    carried: new CylinderGeometry(0.145, 0.175, 0.34, 4),
    carriedHandle: new TorusGeometry(0.14, 0.016, 4, 8, Math.PI),
    carriedFlap: new BoxGeometry(0.3, 0.1, 0.14),
  };

  constructor(
    capacity: number = NEIGHBORHOOD_CONFIG.populationRenderCapacity,
  ) {
    this.object.name = "living-neighbourhood-population";
    this.#actors = Array.from({ length: capacity }, (_, index) =>
      this.#createActor(index)
    );
  }

  setPresentations(
    presentations: readonly Readonly<ResidentPresentation>[],
    animationTimeSeconds = 0,
  ): void {
    const bounded = presentations.slice(0, this.#actors.length);
    const presentedIds = new Set(bounded.map((resident) => resident.id));
    for (const actor of this.#actors) {
      if (
        actor.residentId !== null &&
        !presentedIds.has(actor.residentId)
      ) {
        this.#actorByResidentId.delete(actor.residentId);
        actor.residentId = null;
        actor.object.name = "resident-slot:" + actor.slotIndex;
        actor.object.visible = false;
        actor.object.userData = {};
      }
    }
    for (const resident of bounded) {
      let actor = this.#actorByResidentId.get(resident.id);
      if (actor === undefined) {
        actor = this.#actors.find((candidate) =>
          candidate.residentId === null
        );
        if (actor === undefined) {
          break;
        }
        actor.residentId = resident.id;
        this.#actorByResidentId.set(resident.id, actor);
      }
      actor.object.visible = true;
      actor.object.name = "resident:" + resident.id;
      actor.object.position.set(
        resident.position.x,
        resident.position.y,
        resident.position.z,
      );
      actor.object.rotation.y = resident.yawRadians;
      const previousAppearance = actor.object.userData.appearance as
        | CharacterAppearance
        | undefined;
      if (
        previousAppearance === undefined ||
        !sameCharacterAppearance(previousAppearance, resident.appearance)
      ) {
        this.#applyAppearance(actor, resident.appearance);
      }
      this.#applyMotion(actor, resident, animationTimeSeconds);
      actor.object.userData = {
        residentId: resident.id,
        name: resident.name,
        role: resident.role,
        named: resident.named,
        activity: resident.activity,
        expression: resident.expression ?? "neutral",
        appearance: structuredClone(resident.appearance),
      };
    }
  }

  dispose(): void {
    for (const actor of this.#actors) {
      for (const material of Object.values(actor.materials)) {
        material.dispose();
      }
    }
    for (const geometry of Object.values(this.#shared)) {
      geometry.dispose();
    }
    disposeGarmentGeometrySet(this.#garmentGeometries);
    disposeCharacterFeatureGeometrySet(this.#featureGeometries);
    this.#actorByResidentId.clear();
    this.object.removeFromParent();
    this.object.clear();
  }

  #applyAppearance(
    actor: Actor,
    appearance: Readonly<CharacterAppearance>,
  ): void {
    const body =
      APPEARANCE_PRESENTATION_CONFIG.bodyScaleByShape[appearance.bodyShape];
    const face =
      APPEARANCE_PRESENTATION_CONFIG.faceScaleByShape[appearance.faceShape];
    const outerwear = APPEARANCE_PRESENTATION_CONFIG
      .outerwearSilhouetteById[appearance.outerwear];
    const stature =
      APPEARANCE_PRESENTATION_CONFIG.statureScaleById[appearance.stature];
    const { parts, materials } = actor;

    parts.presentationRoot.scale.set(1, stature, 1);
    parts.hips.scale.set(body.hip, 1, body.hip);
    parts.torso.scale.set(
      body.torso * outerwear.torso.width,
      outerwear.torso.height,
      body.depth * outerwear.torso.depth,
    );
    parts.belly.visible = body.belly > 0.25;
    parts.belly.position.z = 0.2 + body.belly * 0.025;
    parts.belly.scale.set(
      body.torso * (0.58 + body.belly * 0.08),
      0.7 + body.belly * 0.1,
      body.depth * (0.26 + body.belly * 0.15),
    );
    parts.leftArm.position.x = 0.31 * body.shoulder;
    parts.rightArm.position.x = -0.31 * body.shoulder;
    const sleeveWidth = body.limb * outerwear.sleeveWidth;
    parts.leftArm.scale.set(sleeveWidth, 1, sleeveWidth);
    parts.rightArm.scale.set(sleeveWidth, 1, sleeveWidth);
    const garmentGeometry = this.#garmentGeometries[appearance.outerwear];
    parts.leftSleeve.geometry = garmentGeometry.sleeve;
    parts.rightSleeve.geometry = garmentGeometry.sleeve;
    parts.leftHand.position.set(
      0,
      garmentGeometry.handOffset.y - 0.048,
      garmentGeometry.handOffset.z + 0.009,
    );
    parts.rightHand.position.copy(parts.leftHand.position);
    parts.leftLeg.position.x = 0.115 * body.hip;
    parts.rightLeg.position.x = -0.115 * body.hip;
    const legWidth =
      body.limb * (appearance.lowerGarment === "wide_trousers" ? 1.18 : 1);
    parts.leftLeg.scale.set(legWidth, 1, legWidth);
    parts.rightLeg.scale.set(legWidth, 1, legWidth);
    parts.skirt.visible = appearance.lowerGarment === "skirt";
    parts.skirt.scale.set(body.hip, 1, body.hip);
    applyGarmentLayerSet(
      parts.garments,
      appearance.outerwear,
      body,
    );
    parts.head.scale.set(face.x, face.y, face.z);
    parts.nose.scale.set(
      appearance.faceShape === "angular" ? 1.08 : 0.92,
      appearance.faceShape === "oval" ? 1.12 : 1,
      1,
    );
    const eyeSpacing = appearance.faceShape === "round" ? 0.091 : 0.083;
    parts.leftEye.position.x = eyeSpacing;
    parts.rightEye.position.x = -eyeSpacing;
    parts.leftBrow.position.x = eyeSpacing;
    parts.rightBrow.position.x = -eyeSpacing;

    parts.hair.visible =
      appearance.hairStyle !== "bald" && appearance.headwear !== "headscarf";
    if (appearance.hairStyle !== "bald") {
      parts.hair.geometry = this.#featureGeometries.hair[appearance.hairStyle];
    }
    parts.hair.position.set(0, 1.72, 0);
    parts.hair.scale.set(face.x, face.y, face.z);

    const hasEyewear = appearance.eyewear !== "none";
    parts.leftLens.visible = hasEyewear;
    parts.rightLens.visible = hasEyewear;
    if (hasEyewear) {
      const lensGeometry = appearance.eyewear === "round_glasses"
        ? this.#shared.roundLens
        : this.#shared.squareLens;
      parts.leftLens.geometry = lensGeometry;
      parts.rightLens.geometry = lensGeometry;
      parts.leftLens.position.x = eyeSpacing;
      parts.rightLens.position.x = -eyeSpacing;
      const lensScale = appearance.eyewear === "square_glasses"
        ? { x: 1.12, y: 0.72 }
        : { x: 1, y: 1 };
      parts.leftLens.scale.set(lensScale.x, lensScale.y, 1);
      parts.rightLens.scale.set(lensScale.x, lensScale.y, 1);
    }

    parts.headwear.visible = appearance.headwear !== "none";
    if (appearance.headwear !== "none") {
      parts.headwear.geometry = this.#featureGeometries.headwear[
        appearance.headwear
      ];
    }
    parts.headwear.position.set(0, 1.72, 0);
    parts.headwear.scale.set(face.x, face.y, face.z);
    parts.neckwear.visible = appearance.neckwear !== "none";
    parts.neckwear.scale.set(
      appearance.neckwear === "kerchief" ? 1.12 : 1,
      appearance.neckwear === "kerchief" ? 1.25 : 1,
      1,
    );
    parts.carried.visible = appearance.carriedAccessory !== "none";
    parts.carried.scale.set(
      appearance.carriedAccessory === "tote" ? 0.82 : 1,
      appearance.carriedAccessory === "tote" ? 1.16 : 1,
      appearance.carriedAccessory === "tote" ? 0.42 : 0.48,
    );
    parts.carriedHandle.visible = appearance.carriedAccessory === "tote";
    parts.carriedFlap.visible = appearance.carriedAccessory === "satchel";

    materials.outerwear.color.setHex(appearance.colours.outerwear);
    materials.trim.color
      .setHex(appearance.colours.outerwear)
      .offsetHSL(0, -0.015, -0.045);
    materials.lower.color.setHex(appearance.colours.lower);
    materials.boot.color
      .setHex(appearance.colours.lower)
      .offsetHSL(0, -0.02, -0.11);
    materials.accent.color.setHex(appearance.colours.accent);
    materials.skin.color.setHex(
      APPEARANCE_PRESENTATION_CONFIG.skinToneById[appearance.skinTone],
    );
    materials.hair.color.setHex(
      APPEARANCE_PRESENTATION_CONFIG.hairColourById[appearance.hairColour],
    );
    materials.eyes.color.setHex(
      APPEARANCE_PRESENTATION_CONFIG.eyeColourById[appearance.eyeColour],
    );
    materials.accessory.color.setHex(
      appearance.eyewear === "none"
        ? appearance.colours.accent
        : 0x343735,
    );
  }

  #applyMotion(
    actor: Actor,
    resident: Readonly<ResidentPresentation>,
    animationTimeSeconds: number,
  ): void {
    const { parts } = actor;
    const phaseOffset = stableMotionPhase(resident.id);
    const baseCadence = resident.activity === "walking"
      ? 5.2
      : resident.activity === "working"
        ? 2.4
        : 0.85;
    const cadence = baseCadence *
      (1 + stableMotionVariation(resident.id)) *
      (resident.elderVariant === "none" ? 1 : 0.82);
    const cycle = animationTimeSeconds * cadence + phaseOffset;
    const wave = Math.sin(cycle);
    const armSplay = CHARACTER_PRESENTATION_CONFIG.armRestSplayRadians;
    const baseStoop = resident.elderVariant === "none"
      ? 0
      : CHARACTER_PRESENTATION_CONFIG.elderStoopRadians;

    parts.leftArm.rotation.set(0, 0, armSplay);
    parts.rightArm.rotation.set(0, 0, -armSplay);
    parts.leftArm.position.y = 1.46;
    parts.leftArm.position.z = 0;
    parts.rightArm.position.y = 1.46;
    parts.rightArm.position.z = 0;
    parts.leftLeg.rotation.set(0, 0, 0);
    parts.rightLeg.rotation.set(0, 0, 0);
    parts.leftLeg.position.y = NPC_LEG_PIVOT_HEIGHT_M;
    parts.leftLeg.position.z = 0;
    parts.rightLeg.position.y = NPC_LEG_PIVOT_HEIGHT_M;
    parts.rightLeg.position.z = 0;
    parts.upperBody.rotation.set(baseStoop, 0, 0);
    parts.upperBody.position.set(0, 1.25, 0);
    parts.hips.position.y = 0.83;
    parts.skirt.position.y = 0.43;
    setHeadVerticalOffset(parts, 0);
    parts.carried.position.y = 0.89;
    parts.carriedHandle.position.y = 1.08;
    parts.carriedFlap.position.y = 1.02;
    parts.head.rotation.z = 0;
    parts.skirt.rotation.z = 0;
    parts.leftBrow.rotation.z = -0.08;
    parts.rightBrow.rotation.z = 0.08;
    parts.mouth.rotation.z = Math.PI;
    parts.mouth.scale.set(1, 0.28, 1);
    let motionBobMeters = 0;

    if (resident.activity === "walking") {
      parts.leftArm.rotation.x = -wave * 0.34;
      parts.rightArm.rotation.x = wave * 0.34;
      parts.leftLeg.rotation.x = wave * 0.44;
      parts.rightLeg.rotation.x = -wave * 0.44;
      parts.upperBody.rotation.z = wave * 0.022;
      parts.upperBody.rotation.x = baseStoop + 0.025;
      parts.upperBody.position.x = wave * 0.012;
      parts.head.rotation.z = -wave * 0.012;
      parts.skirt.rotation.z = wave * 0.012;
      motionBobMeters = Math.abs(Math.sin(cycle)) * 0.018;
    } else if (resident.activity === "working") {
      parts.leftArm.rotation.x = -0.42 + wave * 0.13;
      parts.rightArm.rotation.x = -0.3 - wave * 0.13;
      parts.upperBody.rotation.z = wave * 0.018;
      parts.head.rotation.z = -0.025 + wave * 0.012;
      parts.leftBrow.rotation.z = -0.11 + wave * 0.025;
      parts.rightBrow.rotation.z = 0.11 - wave * 0.025;
      motionBobMeters = (wave + 1) * 0.003;
    } else if (resident.activity === "resting") {
      const restDropMeters = 0.27;
      parts.upperBody.position.y -= restDropMeters;
      parts.hips.position.y -= 0.08;
      parts.skirt.position.y -= 0.08;
      setHeadVerticalOffset(parts, -restDropMeters);
      parts.leftArm.position.y -= 0.24;
      parts.rightArm.position.y -= 0.24;
      parts.leftArm.position.z = 0.08;
      parts.rightArm.position.z = 0.08;
      parts.leftArm.rotation.x = -0.55 + wave * 0.035;
      parts.rightArm.rotation.x = -0.55 - wave * 0.035;
      parts.leftLeg.position.z = 0.18;
      parts.rightLeg.position.z = 0.18;
      parts.leftLeg.rotation.x = -1.15;
      parts.rightLeg.rotation.x = -1.15;
      parts.carried.position.y -= 0.22;
      parts.carriedHandle.position.y -= 0.22;
      parts.carriedFlap.position.y -= 0.22;
      motionBobMeters = (wave + 1) * 0.002;
    } else {
      const idleSway = wave * 0.012;
      parts.leftArm.rotation.x = idleSway * 0.6;
      parts.rightArm.rotation.x = -idleSway * 0.6;
      parts.upperBody.rotation.z = idleSway;
      parts.upperBody.position.x = wave * 0.014;
      parts.head.rotation.z = -idleSway * 0.55;
      motionBobMeters = (Math.sin(cycle * 2 - Math.PI * 0.5) + 1) * 0.004;
    }
    const expression = resident.expression ?? "neutral";
    if (expression === "warm") {
      parts.mouth.rotation.z = Math.PI;
      parts.mouth.scale.set(1.08, 0.92, 1);
      parts.leftBrow.rotation.z = -0.045;
      parts.rightBrow.rotation.z = 0.045;
    } else if (expression === "wry") {
      parts.mouth.rotation.z = Math.PI + 0.18;
      parts.mouth.scale.set(0.92, 0.62, 1);
      parts.leftBrow.rotation.z = -0.15;
      parts.rightBrow.rotation.z = 0.035;
    } else if (expression === "concerned") {
      parts.mouth.rotation.z = 0;
      parts.mouth.scale.set(0.94, 0.72, 1);
      parts.leftBrow.rotation.z = 0.09;
      parts.rightBrow.rotation.z = -0.09;
    }
    parts.presentationRoot.position.y = npcGroundOffsetM(
      parts.leftLeg.rotation.x,
      parts.rightLeg.rotation.x,
    ) + motionBobMeters;
  }

  #createActor(index: number): Actor {
    const object = new Group();
    object.name = "resident-slot:" + index;
    object.visible = false;
    const presentationRoot = new Group();
    presentationRoot.name = "resident-presentation:" + index;
    presentationRoot.position.y =
      CHARACTER_PRESENTATION_CONFIG.soleClearanceMeters;

    const materials: ActorMaterials = {
      outerwear: createMaterial("resident-outerwear:" + index, 0x596259),
      lower: createMaterial("resident-lower:" + index, 0x343b3b),
      accent: createMaterial("resident-accent:" + index, 0xa6795b, 0.9),
      skin: createMaterial("resident-skin:" + index, 0xb89a7b, 0.92),
      hair: createMaterial("resident-hair:" + index, 0x5d4a3f, 0.96),
      eyes: createMaterial("resident-eyes:" + index, 0x6f7b7c, 0.82),
      accessory: createMaterial("resident-accessory:" + index, 0x343735, 0.8),
      boot: createMaterial("resident-boot:" + index, 0x242826, 0.88),
      trim: createMaterial("resident-garment-trim:" + index, 0x3f4641, 0.97),
    };
    materials.trim.side = DoubleSide;
    const hips = setShadow(new Mesh(this.#shared.hips, materials.lower));
    hips.name = "hips";
    hips.position.y = 0.83;
    const upperBody = new Group();
    upperBody.name = "upper-body";
    upperBody.position.y = 1.25;
    const torso = setShadow(new Mesh(this.#shared.torso, materials.outerwear));
    torso.name = "torso";
    torso.rotation.y = Math.PI / 8;
    torso.visible = false;
    upperBody.add(torso);
    const garments = createGarmentLayerSet(
      this.#garmentGeometries,
      materials.outerwear,
      materials.trim,
      materials.accent,
      "resident-garments:" + index,
    );
    upperBody.add(garments.object);
    const belly = setShadow(new Mesh(this.#shared.belly, materials.outerwear));
    belly.name = "belly-shape";
    belly.position.set(0, -0.06, 0.22);
    belly.visible = false;
    upperBody.add(belly);
    const leftArm = new Group();
    leftArm.name = "left-arm";
    leftArm.position.set(0.31, 1.46, 0);
    const leftSleeve = setShadow(new Mesh(
      this.#garmentGeometries.wool_coat.sleeve,
      materials.outerwear,
    ));
    leftSleeve.name = "left-sleeve";
    leftArm.add(leftSleeve);
    const leftHand = setShadow(new Mesh(this.#shared.hand, materials.skin));
    leftHand.name = "left-hand";
    leftHand.scale.set(0.86, 1, 0.72);
    leftArm.add(leftHand);
    const rightArm = new Group();
    rightArm.name = "right-arm";
    rightArm.position.set(-0.31, 1.46, 0);
    const rightSleeve = setShadow(new Mesh(
      this.#garmentGeometries.wool_coat.sleeve,
      materials.outerwear,
    ));
    rightSleeve.name = "right-sleeve";
    rightArm.add(rightSleeve);
    const rightHand = setShadow(new Mesh(this.#shared.hand, materials.skin));
    rightHand.name = "right-hand";
    rightHand.scale.set(0.86, 1, 0.72);
    rightArm.add(rightHand);
    const leftLeg = new Group();
    leftLeg.name = "left-leg";
    leftLeg.position.set(0.115, NPC_LEG_PIVOT_HEIGHT_M, 0);
    leftLeg.add(
      setShadow(new Mesh(this.#shared.leg, materials.lower)),
    );
    const leftBoot = setShadow(new Mesh(this.#shared.boot, materials.boot));
    leftBoot.name = "left-boot";
    leftBoot.position.set(0, NPC_BOOT_CENTER_Y_M, NPC_BOOT_CENTER_Z_M);
    leftLeg.add(leftBoot);
    const rightLeg = new Group();
    rightLeg.name = "right-leg";
    rightLeg.position.set(-0.115, NPC_LEG_PIVOT_HEIGHT_M, 0);
    rightLeg.add(
      setShadow(new Mesh(this.#shared.leg, materials.lower)),
    );
    const rightBoot = setShadow(
      new Mesh(this.#shared.boot, materials.boot),
    );
    rightBoot.name = "right-boot";
    rightBoot.position.set(0, NPC_BOOT_CENTER_Y_M, NPC_BOOT_CENTER_Z_M);
    rightLeg.add(rightBoot);
    const skirt = setShadow(new Mesh(this.#shared.skirt, materials.lower));
    skirt.name = "skirt";
    skirt.position.y = 0.43;
    skirt.visible = false;
    const head = setShadow(new Mesh(this.#shared.head, materials.skin));
    head.name = "face";
    head.position.y = 1.72;
    const nose = setShadow(new Mesh(this.#shared.nose, materials.skin));
    nose.name = "nose";
    nose.position.set(0, 1.715, NPC_HEAD_RADIUS_M * 0.99);
    nose.rotation.x = Math.PI * 0.5;
    const mouth = new Mesh(this.#shared.mouth, materials.accessory);
    mouth.name = "mouth";
    mouth.position.set(0, 1.635, NPC_HEAD_RADIUS_M * 0.96);
    mouth.rotation.z = Math.PI;
    const leftEye = new Mesh(this.#shared.eye, materials.eyes);
    leftEye.name = "left-eye";
    leftEye.position.set(0.085, 1.765, NPC_HEAD_RADIUS_M * 0.91);
    const rightEye = new Mesh(this.#shared.eye, materials.eyes);
    rightEye.name = "right-eye";
    rightEye.position.set(-0.085, 1.765, NPC_HEAD_RADIUS_M * 0.91);
    const leftBrow = new Mesh(this.#shared.brow, materials.hair);
    leftBrow.name = "left-brow";
    leftBrow.position.set(0.085, 1.825, NPC_HEAD_RADIUS_M * 0.94);
    leftBrow.rotation.z = -0.08;
    const rightBrow = new Mesh(this.#shared.brow, materials.hair);
    rightBrow.name = "right-brow";
    rightBrow.position.set(-0.085, 1.825, NPC_HEAD_RADIUS_M * 0.94);
    rightBrow.rotation.z = 0.08;
    const hair = setShadow(new Mesh(
      this.#featureGeometries.hair.parted,
      materials.hair,
    ));
    hair.name = "hair";
    hair.position.y = 1.72;
    const leftLens = new Mesh(this.#shared.roundLens, materials.accessory);
    leftLens.name = "left-lens";
    leftLens.position.set(0.085, 1.765, NPC_HEAD_RADIUS_M * 1.04);
    const rightLens = new Mesh(this.#shared.roundLens, materials.accessory);
    rightLens.name = "right-lens";
    rightLens.position.set(-0.085, 1.765, NPC_HEAD_RADIUS_M * 1.04);
    const headwear = setShadow(
      new Mesh(this.#featureGeometries.headwear.knit_cap, materials.accent),
    );
    headwear.name = "headwear";
    headwear.position.y = 1.72;
    const neckwear = setShadow(
      new Mesh(this.#shared.neckwear, materials.accent),
    );
    neckwear.name = "neckwear";
    neckwear.position.set(0, 1.52, 0);
    neckwear.rotation.x = Math.PI * 0.5;
    const carried = setShadow(
      new Mesh(this.#shared.carried, materials.accent),
    );
    carried.name = "carried-accessory";
    carried.position.set(0.36, 0.89, -0.03);
    const carriedHandle = setShadow(
      new Mesh(this.#shared.carriedHandle, materials.accent),
    );
    carriedHandle.name = "carried-handle";
    carriedHandle.position.set(0.36, 1.08, -0.03);
    carriedHandle.scale.set(0.78, 1.08, 1);
    carriedHandle.visible = false;
    const carriedFlap = setShadow(
      new Mesh(this.#shared.carriedFlap, materials.accent),
    );
    carriedFlap.name = "carried-flap";
    carriedFlap.position.set(0.36, 1.02, 0.035);
    carriedFlap.visible = false;

    presentationRoot.add(
      hips,
      upperBody,
      leftArm,
      rightArm,
      leftLeg,
      rightLeg,
      skirt,
      head,
      nose,
      mouth,
      leftBrow,
      rightBrow,
      leftEye,
      rightEye,
      hair,
      leftLens,
      rightLens,
      headwear,
      neckwear,
      carried,
      carriedHandle,
      carriedFlap,
    );
    object.add(presentationRoot);
    this.object.add(object);
    return {
      object,
      materials,
      parts: {
        presentationRoot,
        upperBody,
        hips,
        torso,
        garments,
        belly,
        leftArm,
        rightArm,
        leftSleeve,
        rightSleeve,
        leftHand,
        rightHand,
        leftLeg,
        rightLeg,
        skirt,
        head,
        nose,
        mouth,
        leftBrow,
        rightBrow,
        leftEye,
        rightEye,
        hair,
        leftLens,
        rightLens,
        headwear,
        neckwear,
        carried,
        carriedHandle,
        carriedFlap,
      },
      slotIndex: index,
      residentId: null,
    };
  }

}
